import {
  type AccountInfo,
  type IPublicClientApplication,
  InteractionRequiredAuthError
} from "@azure/msal-browser";
import { config } from "@/config/environment";
import { authLogger } from "@/utilities/auth-logger";
import { tokenClaimExtractor } from "@/utilities/token-validator";
import {
  isAccountTypeError,
  handleAccountTypeError
} from "@/utilities/msal-utils";
import { type UserInfo } from "@/types/core";

/**
 * Skip the MSAL access-token cache and request a new AT from Entra.
 * Required after registration: the cached AT predates group assignment.
 */
export type AcquireTokenOptions = {
  forceRefresh?: boolean;
};

/**
 * Centralized MSAL service to eliminate duplications across hooks.
 * Public API is consumed by use-msal-token, addAuthHeader, api-utils, and RTK slices.
 */
export class MsalService {
  private static instance: MsalService;
  private msalInstance: IPublicClientApplication;
  private lastTokenRefresh: number = 0;
  private readonly minRefreshInterval = 30000; // 30 seconds
  /** In-flight force-refresh so parallel API calls wait for the new AT. */
  private inFlightForceRefresh: Promise<string | null> | null = null;

  private constructor(msalInstance: IPublicClientApplication) {
    this.msalInstance = msalInstance;
  }

  static getInstance(msalInstance: IPublicClientApplication): MsalService {
    if (!MsalService.instance) {
      MsalService.instance = new MsalService(msalInstance);
    }
    return MsalService.instance;
  }

  /**
   * Get active account with fallback logic
   */
  getActiveAccount(): AccountInfo | null {
    const activeAccount = this.msalInstance.getActiveAccount();
    if (activeAccount) {
      return activeAccount;
    }

    // If no active account but we have accounts, try to restore the first one
    // This helps with cases where the active account was cleared due to token issues
    const accounts = this.msalInstance.getAllAccounts();
    if (accounts.length > 0) {
      const firstAccount = accounts[0];
      if (config.debug.enabled) {
        console.log(
          "🔄 Restoring active account from available accounts:",
          firstAccount.username
        );
      }
      this.msalInstance.setActiveAccount(firstAccount);
      return firstAccount;
    }

    return null;
  }

  /**
   * Set active account with logging
   */
  setActiveAccount(account: AccountInfo | null): void {
    this.msalInstance.setActiveAccount(account);
    if (config.debug.enabled && account) {
      authLogger.debug("Active account set", { username: account.username });
    }
  }

  /**
   * Get all accounts from MSAL cache
   */
  getAllAccounts(): AccountInfo[] {
    return this.msalInstance.getAllAccounts();
  }

  /**
   * Centralized token acquisition with error handling.
   * Pass `forceRefresh` after Entra group membership changes so MSAL does
   * not return a still-valid cached AT issued before the user joined a role group.
   */
  async acquireTokenSilent(
    account: AccountInfo,
    options?: AcquireTokenOptions
  ): Promise<string | null> {
    const forceRefresh = options?.forceRefresh === true;

    // Dashboard queries that start while a post-registration refresh is in
    // flight must wait for the new AT instead of attaching the cached one.
    if (!forceRefresh && this.inFlightForceRefresh) {
      return this.inFlightForceRefresh;
    }

    if (forceRefresh) {
      if (!this.inFlightForceRefresh) {
        this.inFlightForceRefresh = this.acquireTokenSilentInternal(
          account,
          true
        ).finally(() => {
          this.inFlightForceRefresh = null;
        });
      }
      return this.inFlightForceRefresh;
    }

    return this.acquireTokenSilentInternal(account, false);
  }

  private async acquireTokenSilentInternal(
    account: AccountInfo,
    forceRefresh: boolean
  ): Promise<string | null> {
    try {
      const now = Date.now();
      const timeSinceLastRefresh = now - this.lastTokenRefresh;

      if (
        !forceRefresh &&
        timeSinceLastRefresh < this.minRefreshInterval &&
        this.lastTokenRefresh > 0
      ) {
        if (config.debug.enabled) {
          console.log(
            "Token refresh skipped - too recent:",
            timeSinceLastRefresh,
            "ms ago"
          );
        }
      }

      const tokenResponse = await this.msalInstance.acquireTokenSilent({
        scopes: config.msal.tokenScopes,
        account: account,
        forceRefresh
      });

      this.lastTokenRefresh = now;

      if (config.debug.enabled) {
        console.log(
          forceRefresh
            ? "✅ Token force-refreshed successfully"
            : "✅ Token acquired successfully"
        );
      }

      return tokenResponse.accessToken;
    } catch (error) {
      return this.handleTokenError(error, account);
    }
  }

  /**
   * Renews the access token. Pass `forceRefresh` after group membership
   * changes (registration) so MSAL does not return a still-valid cached AT
   * that was issued before the user joined their role group.
   */
  async refreshToken(
    account: AccountInfo,
    options?: AcquireTokenOptions
  ): Promise<boolean> {
    const currentAccount = this.msalInstance.getActiveAccount();
    if (
      !currentAccount ||
      currentAccount.homeAccountId !== account.homeAccountId
    ) {
      this.setActiveAccount(account);
    }

    const token = await this.acquireTokenSilent(account, options);
    return token !== null;
  }

  /**
   * Centralized error handling for token operations
   */
  private async handleTokenError(
    error: unknown,
    account: AccountInfo
  ): Promise<string | null> {
    if (isAccountTypeError(error)) {
      if (config.debug.enabled) {
        console.warn(
          `⚠️ AADSTS500207 account type error detected on account ${account.username}, handling...`
        );
      }
      await handleAccountTypeError(this.msalInstance, error);
      return null;
    }

    if (error instanceof InteractionRequiredAuthError) {
      if (config.debug.enabled) {
        console.log("🔄 Token requires interaction, clearing account state");
      }
      // Clear account state instead of forcing redirect
      // Let the authentication flow handle the redirect naturally
      this.msalInstance.setActiveAccount(null);
    } else {
      if (config.debug.enabled) {
        console.error("❌ Token operation failed:", error);
      }
    }

    return null;
  }

  /**
   * Centralized session management
   */
  startSession(account: AccountInfo): void {
    const userInfo = tokenClaimExtractor.extractBasicUserInfo(account);
    authLogger.sessionRestore("Session started", {
      userId: userInfo.userId || account.username
    });
  }

  /**
   * Centralized user info extraction with access token
   */
  async getUserInfoWithAccessToken(account: AccountInfo): Promise<UserInfo> {
    try {
      const accessToken = await this.acquireTokenSilent(account);
      if (accessToken) {
        return tokenClaimExtractor.extractUserInfoWithAccessToken(
          account,
          accessToken
        );
      }
    } catch (error) {
      authLogger.warn("Failed to get user info with access token", {
        error: String(error)
      });
    }

    return tokenClaimExtractor.extractBasicUserInfo(account);
  }
}
